import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogActions
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  BusinessOutlined,
  Psychology,
  Hub,
  Speed,
  Security,
  Bolt,
  TrendingUp,
  CheckCircleOutline
} from '@mui/icons-material';
import { API_BASE } from "../api/index.js";

// Branding Palette from Login
const BRAND = {
  primary: "#008080", // Teal
  secondary: "#034A7F", // Navy
  textMain: "#1B2559",
  textSecondary: "#A3AED0",
  bgLight: "#F4F7FE",
  white: "#FFFFFF"
};

export default function Register() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- EXISTING FUNCTIONALITY PRESERVED ---
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    tenantId: "",
    roleId: 2,
    companyName: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Success Modal State
  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    if (!formData.companyName.trim()) newErrors.companyName = "Company name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName,
      };
      const response = await fetch(`${API_BASE}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Registration failed");
      
      setFormData({
        firstName: "", lastName: "", email: "", password: "",
        confirmPassword: "", tenantId: "", roleId: 2, companyName: "",
      });

      // Show the success modal
      setOpenSuccessModal(true);

    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Reusable input style exactly matched from login.js
  const inputSx = { 
    "& .MuiOutlinedInput-root": { borderRadius: "14px", bgcolor: BRAND.bgLight, border: "none" },
    "& fieldset": { border: "none" }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: BRAND.white, overflow: "hidden" }}>
      
      {/* SUCCESS MODAL */}
      <Dialog 
        open={openSuccessModal} 
        disableEscapeKeyDown
        PaperProps={{
          sx: { borderRadius: "24px", p: 3, textAlign: "center", maxWidth: "400px", boxShadow: "0px 20px 40px rgba(0,0,0,0.1)" }
        }}
      >
        <DialogContent sx={{ pb: 1 }}>
          <CheckCircleOutline sx={{ fontSize: 72, color: BRAND.primary, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: BRAND.textMain, mb: 1, letterSpacing: "-0.5px" }}>
            Account Created!
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND.textSecondary, lineHeight: 1.6 }}>
            Your account has been successfully registered. You can now sign in to access the Live Sandbox.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{
              bgcolor: BRAND.secondary, color: "white", py: 1.5, px: 4, borderRadius: "14px",
              textTransform: "none", fontSize: "1rem", fontWeight: 700,
              boxShadow: "0px 8px 16px rgba(3, 74, 127, 0.2)",
              "&:hover": { bgcolor: BRAND.primary, boxShadow: "none" },
            }}
          >
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>

      {/* LEFT SECTION: EXACT FLEX & PADDING FROM LOGIN.JS */}
      <Box sx={{ 
        flex: { xs: 1, md: 1, lg: 0.8 }, 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center",
        alignItems: "center", 
        px: { xs: 3, sm: 8, md: 10, lg: 15 },
        position: "relative",
        zIndex: 10
      }}>
        <Box sx={{ width: "100%", maxWidth: "420px" }}>
          
          {/* Logo - Exact margins from login.js */}
          <Box sx={{ mb: 6, textAlign: isMobile ? 'center' : 'left' }}>
            <a href="https://lendingx.ai" target="_blank" rel="noopener noreferrer" style={{ display: "inline-block" }}>
              <img 
                src="https://lendingx.ai/wp-content/uploads/2026/03/cropped-Png-300x79.png" 
                alt="LendingX Logo" 
                style={{ marginBottom: "32px" }} 
              />
            </a>
            <Typography variant="h4" sx={{ fontWeight: 800, color: BRAND.textMain, mb: 1, letterSpacing: "-1px" }}>
              Create Account
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.textSecondary, fontWeight: 500 }}>
              Move Faster. Lend Smarter.
            </Typography>
          </Box>

          {/* Form Fields - Stack spacing=3 to match login.js exactly */}
          <Stack spacing={3}>
            {/* Name Row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box flex={1}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>First Name</Typography>
                <TextField fullWidth placeholder="John" name="firstName" value={formData.firstName} onChange={handleChange} error={!!errors.firstName} helperText={errors.firstName} sx={inputSx} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Last Name</Typography>
                <TextField fullWidth placeholder="Doe" name="lastName" value={formData.lastName} onChange={handleChange} error={!!errors.lastName} helperText={errors.lastName} sx={inputSx} />
              </Box>
            </Stack>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Company Name</Typography>
              <TextField fullWidth placeholder="Financial Corp" name="companyName" value={formData.companyName} onChange={handleChange} error={!!errors.companyName} helperText={errors.companyName}
                InputProps={{ startAdornment: <InputAdornment position="start"><BusinessOutlined sx={{ color: BRAND.textSecondary, fontSize: 20 }} /></InputAdornment> }} sx={inputSx} />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Business Email</Typography>
              <TextField fullWidth placeholder="mail@lendingx.ai" name="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: BRAND.textSecondary, fontSize: 20 }} /></InputAdornment> }} sx={inputSx} />
            </Box>

            {/* Password Row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box flex={1}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Password</Typography>
                <TextField fullWidth type={showPassword ? "text" : "password"} placeholder="Min. 8 chars" name="password" value={formData.password} onChange={handleChange} error={!!errors.password} helperText={errors.password}
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: BRAND.textSecondary, fontSize: 20 }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }} sx={inputSx} />
              </Box>
              <Box flex={1}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Confirm Password</Typography>
                <TextField fullWidth type={showPassword ? "text" : "password"} placeholder="Confirm" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} error={!!errors.confirmPassword} helperText={errors.confirmPassword} sx={inputSx} />
              </Box>
            </Stack>
          </Stack>

          {errors.general && (
            <Fade in={!!errors.general}>
              <Box sx={{ p: 2, mt: 3, bgcolor: "#FFF5F5", border: "1px solid #FFD1D1", borderRadius: "12px" }}>
                <Typography variant="body2" color="#E53E3E" textAlign="center" fontWeight="600">{errors.general}</Typography>
              </Box>
            </Fade>
          )}

          {/* Button styling matches login.js EXACTLY (my: 3 is standard) */}
          <Box sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              disabled={loading}
              onClick={handleRegister}
              sx={{
                bgcolor: BRAND.secondary,
                color: "white",
                py: 2,
                borderRadius: "14px",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 700,
                boxShadow: "0px 12px 24px rgba(3, 74, 127, 0.25)",
                transition: "all 0.3s ease",
                "&:hover": { bgcolor: BRAND.primary, boxShadow: "none", transform: "translateY(-1px)" },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Register Now"}
            </Button>
          </Box>

          <Typography variant="body2" align="center" sx={{ mt: 5, color: BRAND.textSecondary, fontWeight: 600 }}>
            Already have an account? <Link onClick={() => navigate("/login")} sx={{ color: BRAND.primary, cursor: "pointer", fontWeight: 800, textDecoration: "none" }}>Login here</Link>
          </Typography>
        </Box>

        {/* <Typography variant="caption" sx={{ position: "absolute", bottom: 24, color: BRAND.textSecondary, fontWeight: 500 }}>
          © 2026 LendingX. Institutional Use Only.
        </Typography> */}
      </Box>

      {/* RIGHT SECTION: EXACT FLEX FROM LOGIN.JS */}
      <Box sx={{ 
        flex: { xs: 0, md: 1, lg: 1.2 }, 
        display: { xs: "none", md: "flex" },
        position: "relative",
        background: `linear-gradient(145deg, ${BRAND.secondary} 0%, #011627 100%)`,
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        p: 6
      }}>
        {/* Animated Mesh Background */}
        <Box sx={{
          position: "absolute", width: "100%", height: "100%",
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
          backgroundSize: "40px 40px",
          animation: "meshMovement 60s linear infinite",
          "@keyframes meshMovement": { "0%": { transform: "translate(0, 0)" }, "100%": { transform: "translate(-40px, -40px)" } }
        }} />

        <Box sx={{
          position: "absolute", top: "20%", left: "10%", width: "300px", height: "300px",
          bgcolor: BRAND.primary, filter: "blur(120px)", opacity: 0.15, borderRadius: "50%"
        }} />

        <Box sx={{ position: "relative", zIndex: 5, maxWidth: "540px", width: "100%" }}>
          <Box sx={{ 
            bgcolor: "rgba(255, 255, 255, 0.03)",
            backdropFilter: "blur(20px)",
            borderRadius: "48px",
            p: 6,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 40px 80px rgba(0,0,0,0.3)",
            position: "relative"
          }}>
            
            <Psychology sx={{ fontSize: 64, color: BRAND.primary, mb: 3 }} />
            
            <Typography variant="h3" sx={{ 
              color: BRAND.white, fontWeight: 800, mb: 2, lineHeight: 1.1, letterSpacing: "-1.5px" 
            }}>
              AI-Powered <br /> Credit Intelligence
            </Typography>
            
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
              Unlock hyper-accurate lending decisions with real-time behavioral insights and predictive modeling.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
              {[
                { label: '360° Data Unification', icon: <Hub sx={{ fontSize: 14 }} /> },
                { label: 'Risk Engine', icon: <Speed sx={{ fontSize: 14 }} /> },
                { label: 'Fraud Shield', icon: <Security sx={{ fontSize: 14 }} /> },
                { label: 'Instant Decisions', icon: <Bolt sx={{ fontSize: 14 }} /> },
                { label: 'High-Conversion Prospecting', icon: <TrendingUp sx={{ fontSize: 14 }} /> }
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    px: 2, py: 1, borderRadius: "30px",
                    bgcolor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)"
                  }}
                >
                  <Box sx={{ color: BRAND.primary }}>{item.icon}</Box>
                  <Typography sx={{ color: BRAND.white, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.5px" }}>
                    {item.label.toUpperCase()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}